import * as React from 'react';
import classNames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import throttle from 'lodash/throttle';

import ContextMenuItem from './ContextMenuItem';

import { calculateCoordinatesWithPosition } from 'utils/contextMenu';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import type { ContextMenuItem as ContextMenuItemType, Data } from 'types';

type Props = {
  items: Array<ContextMenuItemType>,
  position: 'bottom' | 'top',
  zIndex?: number,
  closeOnScroll?: boolean,
  target: HTMLElement,
  data?: Data,
  onClose: (event: React.MouseEvent | Event) => void
};

type State = {
  menuCoordinates: {
    x: number,
    y: number
  },
  zIndex?: number,
  position: string
};

class ContextMenu extends React.PureComponent<Props, State> {
  menu: HTMLElement | undefined | null;

  arrow: HTMLElement | undefined | null;

  throttledPositionUpdate: () => void;

  constructor(props: Props) {
    super(props);

    const zIndex = props.closeOnScroll ? 98 : props.zIndex;

    this.state = {
      menuCoordinates: {
        x: -1000,
        y: -1000
      },
      zIndex,
      position: props.position
    };

    this.throttledPositionUpdate = throttle(this.updatePosition, 300);
  }

  componentDidMount(): void {
    const { closeOnScroll } = this.props;

    window.addEventListener('resize', this.throttledPositionUpdate);

    if (closeOnScroll) {
      this.addScrollListeners();
    }

    this.updatePosition();
  }

  componentWillUnmount() {
    const { closeOnScroll } = this.props;

    window.removeEventListener('resize', this.throttledPositionUpdate);

    if (closeOnScroll) {
      this.removeScrollListeners();
    }
  }

  dispatchTrackClick(item: ContextMenuItemType) {
    addUserAction({
      eventCategory: 'context-menu',
      eventAction: kebabCase(item.text || item.type as string)
    });
  }

  addScrollListeners = (): void => {
    window.addEventListener('click', this.handleCloseAll);
    window.addEventListener('wheel', this.handleCloseAll);
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') {
        return;
      }

      this.handleCloseAll(event);
    });
  };

  removeScrollListeners = (): void => {
    window.removeEventListener('click', this.handleCloseAll);
    window.removeEventListener('wheel', this.handleCloseAll);
    window.removeEventListener('keydown', this.handleCloseAll);
  };

  updatePosition = () => {
    const { target, position: currentPosition } = this.props;

    const coordinates = calculateCoordinatesWithPosition(target, this.menu, this.arrow, currentPosition);
    const { x, y, position } = coordinates || {};

    this.setState({
      menuCoordinates: {
        x: x ?? 0,
        y: y ?? 0
      },
      position: position || ''
    });
  };

  handleCloseAll = (event: React.MouseEvent | Event) => {
    const { onClose } = this.props;

    onClose(event);
  };

  handleItemClosed = (item: ContextMenuItemType, event: React.MouseEvent) => {
    this.dispatchTrackClick(item);
    this.handleCloseAll(event);
  };

  render(): React.ReactNode {
    const { items, data } = this.props;
    const { position, menuCoordinates, zIndex } = this.state;

    const arrowContainerClass = classNames({
      'context-menu__arrow-container': true,
      [`context-menu__arrow-container--context-${position}`]: true
    });

    return (
      <React.Fragment>
        <div
          className="context-menu-container"
          style={{ zIndex }}
          onClick={this.handleCloseAll}
        />
        <div
          ref={(ref) => { this.menu = ref; }}
          className="context-menu"
          style={{
            top: menuCoordinates.y,
            left: menuCoordinates.x,
            zIndex
          }}
        >
          <div className={arrowContainerClass}>
            <div className="context-menu__arrow" ref={(ref) => { this.arrow = ref; }} />
            <div className="context-menu__arrow-empty" />
          </div>
          <div className="context-menu__container">
            {
              items.map((item: ContextMenuItemType, index: number) => (
                <ContextMenuItem
                  key={`item-${index}`}
                  item={item}
                  data={data}
                  onClose={this.handleItemClosed}
                />
              ))
            }
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default ContextMenu;
